import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ImagePlus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { createSupportTicket, SUPPORT_ERRORS } from '../../api/support';
import DriverShell from './DriverShell';
import DriverSuccessModal from './DriverSuccessModal';
import cardStyles from './DriverCards.module.css';
import formStyles from '../../styles/form.module.css';
import styles from './DriverSupport.module.css';

function friendlyError(err) {
  return SUPPORT_ERRORS[err.message] || err.message || 'Could not raise the ticket — try again.';
}

export default function DriverSupport() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const { notify } = useOrders();

  const [photoUrl, setPhotoUrl] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const canSubmit = subject.trim().length > 0 && description.trim().length > 0;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const ticket = await createSupportTicket(
        { subject: subject.trim(), message: description.trim() },
        user.idToken,
      );
      setTicketId(ticket.ticketId);
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DriverShell>
      <header className={cardStyles.header}>
        <button className={cardStyles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className={cardStyles.headerTitle}>Support</h1>
      </header>

      <main className={cardStyles.content}>
        <section className={cardStyles.card}>
          <p className={cardStyles.cardLabel}>Attach a photo (optional)</p>

          {photoUrl ? (
            <div className={styles.photoPreviewWrap}>
              <img src={photoUrl} alt="Attachment" className={styles.photoPreview} />
              <button
                type="button"
                className={styles.removePhotoBtn}
                aria-label="Remove photo"
                onClick={() => setPhotoUrl(null)}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={styles.uploadBox}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus size={24} strokeWidth={1.5} />
              <span>Tap to upload from gallery</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={styles.hiddenInput}
            onChange={handleFile}
          />
        </section>

        <section className={cardStyles.card}>
          <p className={cardStyles.cardLabel}>Subject</p>
          <input
            type="text"
            className={formStyles.input}
            placeholder="e.g. Missing item in delivery"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </section>

        <section className={cardStyles.card}>
          <p className={cardStyles.cardLabel}>Describe the issue</p>
          <textarea
            className={formStyles.textarea}
            placeholder="Tell us what happened…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </section>
      </main>

      <div className={cardStyles.ctaBar}>
        <button
          type="button"
          className={styles.submitBtn}
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Raising ticket…' : 'Raise Ticket'}
        </button>
      </div>

      {ticketId && (
        <DriverSuccessModal
          icon={CheckCircle2}
          title="Ticket raised!"
          text={`Ticket #${ticketId} has been submitted. Our team will get back to you shortly.`}
          buttonLabel="Back to Home"
          onButtonClick={() => navigate('/driver', { replace: true })}
        />
      )}
    </DriverShell>
  );
}
